import { stringify as stringifyYaml } from 'yaml';
import type { ToolsFrontmatter, ToolDefinition, GlobalControls } from '../types/tools.js';

export interface ToolsGenerationInput {
  agent_id: string;
  code_name: string;
  environment: 'dev' | 'stage' | 'prod';
  owner: string;
  display_name: string;
  enforcement_mode?: 'wrapper' | 'gateway' | 'both';
  logging_redaction?: 'hash-only' | 'redacted' | 'full-local';
  global_controls?: Partial<GlobalControls>;
  tools?: ToolDefinition[];
}

export function generateToolsMd(input: ToolsGenerationInput): string {
  const today = new Date().toISOString().split('T')[0]!;

  const globalControls: GlobalControls = {
    default_network_policy: input.global_controls?.default_network_policy ?? 'deny',
    default_timeout_ms: input.global_controls?.default_timeout_ms ?? 8000,
    default_rate_limit_rpm: input.global_controls?.default_rate_limit_rpm ?? 60,
    default_retries: input.global_controls?.default_retries ?? 2,
    logging_redaction: input.global_controls?.logging_redaction ?? input.logging_redaction ?? 'redacted',
  };

  const frontmatter: ToolsFrontmatter = {
    agent_id: input.agent_id,
    code_name: input.code_name,
    version: '0.1',
    environment: input.environment,
    owner: input.owner,
    last_updated: today,
    enforcement_mode: input.enforcement_mode ?? 'wrapper',
    global_controls: globalControls,
    tools: input.tools ?? [],
  };

  const yaml = stringifyYaml(frontmatter, { lineWidth: 0 });

  const toolsList = frontmatter.tools.length > 0
    ? frontmatter.tools.map((t) =>
        `- **${t.name}** (\`${t.id}\`): ${t.description} [${t.access}, ${t.limits.timeout_ms}ms, ${t.limits.rate_limit_rpm}rpm]`
      ).join('\n')
    : 'No tools configured.';

  return `# TOOLS — ${input.display_name}

---
${yaml}---

Only tools listed here are allowed. Secrets via \`secret_ref://\` only.

${toolsList}

## Git Workflow

Use **git worktrees** for all feature work. Do not switch branches on the main checkout.
Store repositories under \`~/code/\` and create worktrees alongside them for parallel tasks.
`;
}
